import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { startCheckout } from "../../lib/checkout";
import { UPGRADE_GATES } from "../../lib/upgradeGates";
import { BottomSheet, ChalkActionButton } from "./primitives";
import { ChipPicker } from "./drawItUp/ChipPicker";
import { DraftPreview } from "./drawItUp/DraftPreview";

const FALLBACK_CHIPS = [
  "Practice plan", "Game day reminder", "Snack rotation",
  "Field change", "Pep talk", "Volunteer ask", "Cancel & reschedule",
];

/**
 * Draw It Up — Chalk OS AI powered Chalk Talk drafting.
 *
 * Flow:
 *   1. coach picks a chip (or none) + optional extra context
 *   2. presses Generate → POST /ai/draft (live Chalk OS)
 *   3. preview the draft, can Regenerate or Use this
 *   4. Use this → posts to Chalk Talk and closes
 */
export default function DrawItUpSheet({ isOpen, onDismiss, teamId, onPosted }) {
  const [chips, setChips] = useState(FALLBACK_CHIPS);
  const [selectedChip, setSelectedChip] = useState(null);
  const [extra, setExtra] = useState("");
  const [draft, setDraft] = useState("");
  const [stub, setStub] = useState(false);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedChip(null);
    setExtra("");
    setDraft("");
    setStub(false);
    setCopied(false);
    api.aiChips().then((d) => {
      if (d && d.chips) setChips(d.chips);
    }).catch(() => { /* fall back to defaults */ });
  }, [isOpen]);

  async function generate() {
    if (!teamId) return;
    setLoading(true);
    setDraft("");
    try {
      const data = await api.aiDraft(selectedChip || "", extra.trim(), teamId);
      setDraft(data?.draft || "");
      setStub(Boolean(data?.stub));
    } catch (e) {
      if (e.code === "TIER_INSUFFICIENT") {
        // Never surface raw tier errors — drop the user straight into checkout
        // with full analytics context.
        try {
          await startCheckout({
            tier: "chalkboard",
            teamId,
            context: { feature_name: UPGRADE_GATES.draw_it_up.featureName, source_screen: "draw_it_up_sheet", current_tier: "pickup" },
          });
        } catch (err) {
          toast.error(err?.message || "Couldn't open checkout.");
        }
      } else if (e.code === "RATE_LIMITED") {
        toast.error("Hit the AI hourly limit. Try again shortly.");
      } else {
        toast.error(e.message || "Couldn't draft just now. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function useDraft() {
    if (!draft || !teamId || stub) return;
    setPosting(true);
    try {
      await api.postMessage(teamId, {
        body: draft, audience: "everyone", audience_user_ids: [], pinned: false,
      });
      toast.success("Posted to Chalk Talk.");
      onPosted && onPosted(draft);
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't post.");
    } finally {
      setPosting(false);
    }
  }

  function copyDraft() {
    if (!draft) return;
    try {
      navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Draw It Up" onDismiss={onDismiss}>
      <div data-testid="draw-it-up-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13, color: "var(--chalk-muted)" }}>
          Pick a vibe, add a detail, and Chalk OS drafts a coach-voice Chalk Talk in seconds.
        </div>

        <ChipPicker chips={chips} selected={selectedChip} onSelect={setSelectedChip} />

        <div>
          <div style={{
            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
            color: "var(--chalk-muted)", marginBottom: 6,
          }}>
            Add a detail (optional)
          </div>
          <textarea
            data-testid="draw-extra-input"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="e.g. Tuesday 6pm, Field 4, bring cleats"
            rows={2}
            style={{
              width: "100%", border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "10px 12px", fontSize: 14,
              fontFamily: "inherit", resize: "vertical", outline: "none",
              background: "#FBF8F2", color: "var(--chalk-board)",
            }}
          />
        </div>

        {!draft && (
          <ChalkActionButton
            testId="draw-generate"
            variant="primary"
            label={loading ? "Drawing it up…" : "Draw it up"}
            icon={<Sparkles size={16} aria-hidden="true" />}
            loading={loading}
            onClick={generate}
          />
        )}

        {draft && (
          <DraftPreview
            draft={draft}
            stub={stub}
            copied={copied}
            loading={loading}
            posting={posting}
            onCopy={copyDraft}
            onRegenerate={generate}
            onUse={useDraft}
          />
        )}
      </div>
    </BottomSheet>
  );
}
